import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ILocation, fetchHeldeskLocations } from '../../../../libraries/location';
import { AppState } from '../../../client-redux/reducers';
import { useFitTableRows } from '../../../components/util/fit-table-rows-hook';
import { Group, Loader, Table } from '@mantine/core';
import PaginationComponent from '../../../components/pagination';
import Header from '../../../components/util/header';
import HelpdeskFilterBar, { HelpdeskFilterBarFieldsData } from '../../components/helpdesk-filterbar';
import { useStatusOperDialog, isZarphSuccessResponse } from '../../../context/status-oper-dialog';
import { IconEdit } from '@tabler/icons-react';
import './styles.scss';

const HelpdeskLocationList = () => {
  const lang = useSelector((state: AppState) => state.lang.lang);
  const navigate = useNavigate();

  const [list, setList] = useState<ILocation[]>([]);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [offset, setOffset] = useState<number>(0);
  const {
    limit = 10,
    setLimit,
    tableRef
  } = useFitTableRows({
    bufferRows: 0,
    minHeightForDynamic: 400
  });
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [newLocation, setNewLocation] = useState<boolean>(false);
  const [filterBarData, setFilterBarData] = useState<HelpdeskFilterBarFieldsData>();

  const { handleApiError } = useStatusOperDialog();

  useEffect(() => {
    if (filterBarData && limit) {
      loadLocationsList();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBarData, limit, offset]);

  const handleFilterBarOnDataChange = (data: HelpdeskFilterBarFieldsData) => {
    setFilterBarData(data);
    changePage(1);
  };

  const loadLocationsList = () => {
    if (!filterBarData || !limit) return;

    setLoading(true);
    setList([]);
    setTotalRows(0);

    fetchHeldeskLocations(limit, offset, true, filterBarData?.entity, filterBarData?.name)
      .then(response => {
        if (isZarphSuccessResponse(response, [9004])) {
          setList(response?.data?.list || []);
          setTotalRows(response?.data?.totalRows || 0);
        } else {
          console.error('Error response:', response);
          handleApiError(response, lang.ALERT_ERROR_FETCHING_DATA);
          setLoading(false);
        }
      })
      .catch(err => {
        // unexpected errors go here
        handleApiError(err, lang.ALERT_ERROR_FETCHING_DATA);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const changePage = (page: number) => {
    const newOffset = (page - 1) * limit;
    setCurrentPage(page);
    setOffset(newOffset);
  };

  const resultsSelect = (newValue: number) => {
    const newLimit = Number(newValue);
    setLimit(newLimit);
    setCurrentPage(1);
    setOffset(0);
  };

  const toggleNewLocation = () => {
    setNewLocation(!newLocation);
    navigate(`/helpdesk/location/new/0/0`);
  };

  return (
    <div className="helpdesk-locals">
      <Header pageTitle={lang.GLOBAL_LOCATIONS} />
      <HelpdeskFilterBar
        onSearch={loadLocationsList}
        onDataChange={handleFilterBarOnDataChange}
        fieldsUsage={{
          entity: true,
          name: true
        }}
        addButton={toggleNewLocation}
      />
      <Table.ScrollContainer minWidth={500} ref={tableRef}>
        <Table striped highlightOnHover verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{lang.ENTITY_ID}</Table.Th>
              <Table.Th>ID</Table.Th>
              <Table.Th>{lang.GLOBAL_NAME}</Table.Th>
              <Table.Th>{lang.GLOBAL_ADDRESS}</Table.Th>
              <Table.Th>{lang.EVENT_ZIP_CODE}</Table.Th>
              <Table.Th></Table.Th>
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <Loader color="#016273" />
                </Table.Td>
              </Table.Tr>
            ) : !list.length ? (
              <Table.Tr>
                <Table.Td colSpan={6} style={{ textAlign: 'center' }}>
                  {lang.GLOBAL_NO_RESULTS}
                </Table.Td>
              </Table.Tr>
            ) : (
              list.map((item, index) => (
                <Table.Tr
                  key={index}
                  onClick={() => {
                    navigate(`/helpdesk/location/read/${item.entid}/${item.sid}`);
                  }}
                >
                  <Table.Td>{item.entid}</Table.Td>
                  <Table.Td>{item.sid}</Table.Td>
                  <Table.Td>{item.name}</Table.Td>
                  <Table.Td>{item.address}</Table.Td>
                  <Table.Td>{item.postalCode}</Table.Td>
                  <Table.Td>
                    <IconEdit
                      stroke={2}
                      style={{ cursor: 'pointer' }}
                      onClick={event => {
                        event.stopPropagation();
                        navigate(`/helpdesk/location/edit/${item.entid}/${item.sid}`);
                      }}
                    />
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <Group>
        <PaginationComponent
          total={totalRows}
          itemsPerPage={limit}
          dynamicLimit={limit}
          currentPage={currentPage}
          onPageChange={changePage}
          onResultsChange={resultsSelect}
          offset={offset}
        />
      </Group>
    </div>
  );
};

export default HelpdeskLocationList;
